<template>
  <div
    class="sidebar d-flex flex-column sticky-xl-top"
    :class="{ 'sidebar--open': isMobileSidebarOpen }"
  >
    <div
      id="confetti-cta"
      style="
        position: absolute;
        top: 0;
        left: 50%;
        width: 100vh;
        transform: translate(-50%, -50%);
        height: 100vh;
        pointer-events: none;
      "
    ></div>

    <!-- Mobile heading with open/close button -->
    <div
      @click="isMobileSidebarOpen = !isMobileSidebarOpen"
      class="sidebar__mobile-heading d-flex align-items-center d-xl-none"
    >
      <div class="d-flex flex-grow-1 align-items-center">
        <span class="h6 mb-0">Jouw productlijst</span>
        <span class="badge badge--sm ms-2">{{ totalProductsCount }}</span>
      </div>

      <!-- Open button -->
      <button class="btn btn--icon-sm btn--transparent sidebar__button-open">
        <i class="icon icon-chevron"></i>
      </button>

      <!-- Close button -->
      <button class="btn btn--primary btn--sm sidebar__button-close">
        <span>Sluiten</span>
        <i class="icon icon-close"></i>
      </button>
    </div>

    <!-- Content -->
    <div class="sidebar__content row flex-column gy-1 gy-xxl-2">
      <!-- CTA -->
      <div class="col d-none d-xl-block">
        <SidebarCta :flowType="flowType" :currentStep="currentStep" />
      </div>

      <!-- Summary -->
      <div class="col">
        <div class="sidebar__summary sidebar__card mt-2 mt-xl-0">
          <!-- Title -->
          <span class="sidebar__summary-title">
            {{ isBasePackage ? basePackageTitle : 'Eigen samengesteld pakket' }}
          </span>

          <!-- Parcel label -->
          <div v-if="getPackageType === 'small'" class="badge">Brievenbus</div>

          <!-- Quantity & actions -->
          <div class="sidebar__summary-actions gap-05">
            <QuantityInput
              :value="localQuantity"
              :min="10"
              :max="99999"
              :step="1"
              size="sm"
              :aria-label="'Aantal pakketten'"
              @update:value="updateQuantity"
              @quantity-changed="handleQuantityChanged"
              @invalid="handleQuantityInvalid"
              @corrected="handleQuantityCorrected"
            />
            <button
              class="remove btn--transparent btn--sm"
              @click="handleRemovePackage"
              aria-label="Verwijder pakket"
            >
              <i class="icon icon-trashbin"></i>
            </button>
            <div class="d-flex flex-column text-end flex-grow-1">
              <strong class="d-block mb-0">{{
                formatPrice(totalPrice, priceFormat)
              }}</strong>
              <span class="d-block text-gray-500 fs-xs mt-n05">/ pakket</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Steps -->
      <div v-for="(step, index) in currentFlowSteps" :key="step.id" class="col">
        <div
          class="sidebar__step sidebar__card"
          :class="{
            'sidebar__step--completed':
              isStepCompleted(index) && !isActiveStep(index),
            'sidebar__step--active': isActiveStep(index),
          }"
          role="button"
          tabindex="0"
          :aria-expanded="isStepOpen(index).toString()"
        >
          <div
            class="sidebar__step-title"
            @click.prevent="handleStepTitleClick(index)"
            @keydown.enter.prevent="handleStepTitleClick(index)"
            @keydown.space.prevent="handleStepTitleClick(index)"
          >
            <div class="sidebar__step-badge" aria-hidden="true">
              <i v-if="isStepCompleted(index)" class="icon icon-checkmark"></i>
              <span v-else>{{ index + 1 }}</span>
            </div>
            <strong class="sidebar__step-title-text">{{ step.title }}</strong>
            <i class="icon icon-chevron"></i>
          </div>
          <transition name="fade">
            <div v-show="isStepOpen(index)" class="sidebar__collapse">
              <template v-if="step.sidebarType === 'base'">
                <SidebarSingleSection
                  :item="data.basePackage"
                  :clickable="true"
                  type="base"
                  emptyText="Geen basispakket gekozen"
                  :showRemoveButton="false"
                  @edit="goToStep(index)"
                  @show-modal="
                    (product, type) =>
                      openNewModal({
                        product: product,
                        count: product?.count || 1,
                        type: type,
                      })
                  "
                />
              </template>
              <template v-else-if="step.sidebarType === 'products'">
                <SidebarListSection
                  :items="localProducts"
                  :clickable="true"
                  type="products"
                  emptyText="Nog geen producten gekozen"
                  @increase="increaseProductCount"
                  @decrease="decreaseProductCount"
                  @remove="removeProduct"
                  @show-modal="
                    (product, type) =>
                      openNewModal({
                        product: product,
                        count: product?.count || 1,
                        type: type,
                      })
                  "
                />
              </template>
              <template v-else-if="step.sidebarType === 'foodDrinks'">
                <SidebarListSection
                  :items="localFoodDrinks"
                  :clickable="true"
                  type="foodDrinks"
                  emptyText="Nog geen eten & drinken gekozen"
                  @increase="increaseFoodDrinkCount"
                  @decrease="decreaseFoodDrinkCount"
                  @remove="removeFoodDrink"
                  @show-modal="
                    (product, type) =>
                      openNewModal({
                        product: product,
                        count: product?.count || 1,
                        type: type,
                      })
                  "
                />
              </template>
              <template v-else-if="step.sidebarType === 'packaging'">
                <div
                  v-if="
                    data.packagingMode === 'product' && data.packagingProduct
                  "
                  class="row flex-column gy-1 mt-0"
                >
                  <div class="col">
                    <div class="sidebar__item row gx-1 align-items-center">
                      <div v-if="data.packagingProduct.image" class="col-auto">
                        <figure
                          class="image clickable"
                          @click="
                            openNewModal({
                              product: data.packagingProduct,
                              selected: true,
                              type: 'packaging',
                            })
                          "
                        >
                          <img
                            :src="data.packagingProduct.images?.[0]?.medium"
                            :alt="data.packagingProduct.name"
                          />
                        </figure>
                      </div>
                      <div class="col d-flex flex-column overflow-x-hidden">
                        <span class="name fs-sm">{{
                          data.packagingProduct.name
                        }}</span>
                        <div class="d-flex align-items-center mt-05 gap-05">
                          <button
                            class="btn btn--gray btn--sm"
                            @click="goToStep(index)"
                            aria-label="Edit packaging"
                          >
                            Wijzigen
                          </button>
                          <button
                            class="remove"
                            @click.stop="$emit('remove-packaging')"
                            aria-label="Remove packaging"
                          >
                            <i class="icon icon-trashbin ms-0"></i>
                          </button>
                          <span
                            v-if="data.packagingProduct.price > 0"
                            class="price mt-05 flex-grow-1 text-end"
                          >
                            {{ formatPrice(data.packagingProduct.price) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-else-if="data.packagingMode === 'box' && data.packaging"
                  class="row flex-column gy-1 mt-0"
                >
                  <div class="col">
                    <div class="sidebar__item row gx-1 align-items-center">
                      <div v-if="data.packaging.image" class="col-auto">
                        <figure
                          class="image clickable"
                          @click="
                            openNewModal({
                              product: data.packaging,
                              selected: true,
                              type: 'packaging',
                            })
                          "
                        >
                          <img
                            :src="data.packaging.image"
                            :alt="data.packaging.name"
                          />
                        </figure>
                      </div>
                      <div class="col d-flex flex-column overflow-x-hidden">
                        <span class="name fs-sm">{{
                          data.packaging.name
                        }}</span>
                        <div class="d-flex align-items-center mt-05 gap-05">
                          <button
                            class="btn btn--gray btn--sm"
                            @click="goToStep(index)"
                            aria-label="Edit packaging"
                          >
                            Wijzigen
                          </button>
                          <button
                            class="remove"
                            @click.stop="$emit('remove-packaging')"
                            aria-label="Remove packaging"
                          >
                            <i class="icon icon-trashbin ms-0"></i>
                          </button>
                          <span
                            v-if="data.packaging.price > 0"
                            class="price mt-05 flex-grow-1 text-end"
                          >
                            {{ formatPrice(data.packaging.price) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="row flex-column gy-1 mt-0">
                  <div class="col">
                    <div class="empty p-3">
                      <i class="icon icon-search text-primary fs-2xl"></i>
                      <p class="d-block fs-sm lh-sm my-0 text-black">
                        Nog geen verpakking gekozen
                      </p>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else-if="step.sidebarType === 'personalization'">
                <!-- Personalized Products -->
                <SidebarSingleSection
                  v-if="hasPersonalizedProducts()"
                  :item="getPersonalizedProductsItem()"
                  type="personalization"
                  emptyText=""
                  :showRemoveButton="false"
                  :showEditButton="true"
                  @edit="goToStep(index)"
                />

                <!-- Selected Card -->
                <SidebarSingleSection
                  v-if="hasPersonalizedProducts()"
                  :item="getSelectedCardItem()"
                  :clickable="true"
                  type="card"
                  emptyText=""
                  :showRemoveButton="true"
                  :showEditButton="true"
                  @edit="goToStep(index)"
                  @remove="removeItem(null, 'card')"
                  @show-modal="
                    (product, type) =>
                      openNewModal({
                        product: product,
                        selected: true,
                        type: type,
                      })
                  "
                />

                <!-- Empty result -->
                <div class="empty p-3 mt-2" v-if="!hasPersonalizedProducts()">
                  <i class="icon icon-search text-primary fs-2xl"></i>
                  <p class="d-block fs-sm lh-sm my-0 text-black">
                    Nog geen personalisatie gekozen
                  </p>
                </div>
              </template>
            </div>
          </transition>
          <transition name="fade">
            <div
              class="sidebar__button d-none"
              :class="{ 'd-xl-block': isStepOpen(index) }"
            >
              <div v-if="hasNextStep(index)">
                <button
                  class="btn w-100"
                  :class="
                    isStepCompleted(currentStep) ? 'btn--cta' : 'btn--gray'
                  "
                  @click.stop.prevent="goToNextStep(index)"
                  aria-label="Ga naar volgende stap"
                >
                  {{ getNextStepTitle(index) }}
                  <i class="icon icon-arrow"></i>
                </button>
              </div>
              <div v-else-if="isLastStep(index)">
                <button
                  @click="handleAddToCart"
                  class="btn btn--cta w-100"
                  :aria-label="
                    isEditMode
                      ? 'Pakket aanpassen'
                      : 'Toevoegen aan winkelmandje'
                  "
                >
                  {{ isEditMode ? 'Pakket aanpassen' : 'In winkelmandje' }}
                  <i
                    :class="isEditMode ? 'icon icon-edit' : 'icon icon-bag-add'"
                  ></i>
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Mobile footer with next step button -->
    <div class="sidebar-mobile__footer d-flex d-xl-none mt-2">
      <!-- Price -->
      <div class="d-flex flex-column flex-grow-1">
        <span class="d-block text-gray-500 fs-xs">Prijs per pakket</span>
        <strong class="d-block h6 mb-0">{{
          formatPrice(totalPrice, priceFormat)
        }}</strong>
      </div>

      <!-- Button next step -->
      <button
        v-if="hasNextStep(currentStep)"
        class="btn"
        :class="isStepCompleted(currentStep) ? 'btn--cta' : 'btn--gray'"
        @click.stop.prevent="goToNextStep(currentStep)"
        aria-label="Ga naar volgende stap"
      >
        {{ getNextStepTitle(currentStep) }}
        <i class="icon icon-arrow"></i>
      </button>
      <button
        v-else-if="isLastStep(currentStep)"
        @click="handleAddToCart"
        class="btn btn--cta"
        :aria-label="
          isEditMode ? 'Pakket aanpassen' : 'Toevoegen aan winkelmandje'
        "
      >
        {{ isEditMode ? 'Pakket aanpassen' : 'In winkelmandje' }}
        <i :class="isEditMode ? 'icon icon-edit' : 'icon icon-bag-add'"></i>
      </button>
    </div>
  </div>

  <!-- Detail modal -->
  <ProductDetailModal
    v-if="showProductDetailModal"
    v-for="(modal, index) in detailModalDetails"
    :zIndex="index"
    :key="index"
    :product="modal.product"
    :count="modal.count || getItemCount(modal.product?.id, modal.type) || 0"
    :type="modal.type"
    :isPackage="modal.type === 'base'"
    :isPackaging="modal.type === 'packaging'"
    :isPersonalizedCard="modal.type === 'card'"
    :selected="modal.selected !== null ? modal.selected : true"
    @close-modal="closeForegroundModal(index)"
    @decrease="
      updateItemCount(modal.product, modal.type, 'decrease');
      modal.count = getItemCount(modal.product?.id, modal.type);
    "
    @increase="
      updateItemCount(modal.product, modal.type, 'increase');
      modal.count = getItemCount(modal.product?.id, modal.type);
    "
    @remove="removeItem(modal.product?.id, modal.type)"
    @add-modal="(newModalData) => openNewModal(newModalData)"
    @select="handleDetailModalSelect(modal.product, modal.type)"
  />
</template>

<script>
// Components
import SidebarListSection from './SidebarListSection.vue';
import SidebarSingleSection from './SidebarSingleSection.vue';
import QuantityInput from '../components/QuantityInput.vue';
import SidebarCta from './SidebarCta.vue';
import ProductDetailModal from '../modals/ProductDetailModal.vue';

// Utils
import { flowConfig } from '../config/flowConfig.js';
import {
  formatPrice,
  getCardUnitPrice,
  calculateVatBreakdownByProduct,
  roundCurrency,
} from '../utils/priceUtils.js';
import { decodeHtml } from '../utils/utils.js';
import { calculateTotalQuantity } from '../utils/productCountUtils.js';
import { launchConfetti } from '../utils/confettiUtils.js';

export default {
  components: {
    ProductDetailModal,
    SidebarCta,
    SidebarListSection,
    SidebarSingleSection,
    QuantityInput,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    currentStep: {
      type: Number,
      required: true,
    },
    flowType: {
      type: String,
      required: true,
      validator: (value) => ['compose', 'selectPackage'].includes(value),
    },
    packageQuantity: {
      type: Number,
      required: false,
      default: 1,
    },
    isCustomFlow: {
      type: Boolean,
      default: false,
    },
    cartItemKey: {
      type: Number,
      default: null,
    },
  },
  emits: [
    'increase-product',
    'decrease-product',
    'add-to-cart',
    'go-to-step',
    'reset-package',
    'remove-product',
    'remove-food',
    'remove-packaging',
    'remove-card',
    'update-products',
    'update-fooddrinks',
    'update',
    'update:packageQuantity',
    'clear-draft',
    'show-package-type-modal',
  ],
  data() {
    return {
      localProducts: [],
      localFoodDrinks: [],
      localQuantity: this.packageQuantity,
      openSteps: new Set(),
      isMobileSidebarOpen: false,
      priceFormat: {
        thousandSeparator: '.',
        decimalSeparator: ',',
        decimals: 2,
      },
      stepInitialized: false,
      showProductDetailModal: false,
      detailModalDetails: [],
      _totalPriceCache: null,
      cardPrice: null, // Cached card price based on quantity
    };
  },
  computed: {
    currentFlowSteps() {
      const config = flowConfig[this.flowType];
      if (!config) return [];
      return config.steps.filter((step) => step.sidebarType !== 'none');
    },
    hasPackage() {
      return this.data.basePackage || this.isCustomFlow;
    },
    isBasePackage() {
      return !!this.data.basePackage;
    },
    getPackageType() {
      return this.data.packageType;
    },
    packagingStatus() {
      if (this.data.packagingMode === 'product' && this.data.packagingProduct) {
        return {
          type: 'product',
          item: this.data.packagingProduct,
          hasPackaging: true,
        };
      } else if (this.data.packagingMode === 'box' && this.data.packaging) {
        return {
          type: 'box',
          item: this.data.packaging,
          hasPackaging: true,
        };
      } else {
        return {
          type: 'none',
          item: null,
          hasPackaging: false,
        };
      }
    },
    totalPrice() {
      const cacheKey = this._getTotalPriceCacheKey();

      if (this._totalPriceCache && this._totalPriceCache.key === cacheKey) {
        return this._totalPriceCache.value;
      }

      try {
        const productsPrice = this.getProductsPriceExVat();
        const foodDrinksPrice = this.getFoodDrinksPriceExVat();
        const packagingPrice = this.getSelectedPackagingPriceExVat();
        const personalizationPrice = this.getPersonalizationPriceExVat();

        const total =
          productsPrice +
          foodDrinksPrice +
          packagingPrice +
          personalizationPrice;

        const result = Math.round(total * 100) / 100;

        this._totalPriceCache = { key: cacheKey, value: result };
        return result;
      } catch (error) {
        return 0;
      }
    },

    basePackageTitle() {
      if (this.isBasePackage) {
        const rawTitle =
          this.data.basePackage.title ?? this.data.basePackage.name ?? '';
        return decodeHtml(rawTitle.trim()) || 'Jouw pakket';
      }
      return '';
    },
    openStepsCount() {
      return this.openSteps.size;
    },
    isEditMode() {
      return this.cartItemKey !== null && this.cartItemKey >= 0;
    },
    personalizedProductsCount() {
      if (
        !this.data.personalization ||
        !this.data.personalization.productPersonalization
      ) {
        return 0;
      }

      return Object.values(
        this.data.personalization.productPersonalization,
      ).filter(
        (personalization) =>
          personalization &&
          (personalization.sticker ||
            personalization.zeefdruk ||
            personalization.graveren),
      ).length;
    },
    totalProductsCount() {
      let total = 0;
      if (this.isBasePackage) total += 1;
      total += calculateTotalQuantity(this.localProducts);
      total += calculateTotalQuantity(this.localFoodDrinks);
      if (this.data.packagingMode === 'product' && this.data.packagingProduct)
        total += 1;
      else if (this.data.packagingMode === 'box' && this.data.packaging)
        total += 1;
      return total;
    },
    printingCosts() {
      if (!this.data.personalization?.printing?.breakdown) {
        return null;
      }

      const breakdown = this.data.personalization.printing.breakdown;
      const methods = [];

      if (breakdown.sticker && breakdown.sticker.total > 0) {
        methods.push({
          method: 'Sticker',
          qty: breakdown.sticker.qty,
          unit: breakdown.sticker.unit,
          total: breakdown.sticker.total,
        });
      }

      if (breakdown.zeefdruk && breakdown.zeefdruk.total > 0) {
        methods.push({
          method: 'Zeefdruk',
          qty: breakdown.zeefdruk.qty,
          unit: breakdown.zeefdruk.unit,
          total: breakdown.zeefdruk.total,
        });
      }

      if (breakdown.graveren && breakdown.graveren.total > 0) {
        methods.push({
          method: 'Lasergravure',
          qty: breakdown.graveren.qty,
          unit: breakdown.graveren.unit,
          total: breakdown.graveren.total,
        });
      }

      return methods.length > 0 ? methods : null;
    },
    totalPrintingCosts() {
      if (!this.data.personalization?.printing?.total_ex_vat) {
        return 0;
      }
      // Return total printing costs for all packages
      return this.data.personalization.printing.total_ex_vat;
    },
  },
  created() {
    this.setupEventListeners();
  },
  watch: {
    packageQuantity: {
      immediate: true,
      handler(newVal) {
        this.localQuantity = newVal;
      },
    },
    currentStep: {
      immediate: true,
      handler(newStepIndex) {
        const sidebarStepIndex = this.getSidebarStepIndex(newStepIndex);
        if (sidebarStepIndex !== -1) {
          this.openSteps.clear();
          this.openSteps.add(sidebarStepIndex);
        }

        // Initialize confetti
        if (this.stepInitialized) {
          if (newStepIndex === 2) this.handleLaunchConfetti();
        } else {
          this.stepInitialized = true;
        }
      },
    },
    'data.products': {
      immediate: true,
      handler() {
        this.updateLocalProducts();
      },
    },
    'data.basePackage': {
      immediate: true,
      handler() {
        this.updateLocalProducts();
        this.updateLocalFoodDrinks();
      },
    },
    'data.foodDrinks': {
      immediate: true,
      handler() {
        this.updateLocalFoodDrinks();
      },
    },
    'data.personalization': {
      immediate: true,
      handler() {
        this.clearTotalPriceCache();
        this.updateCardPrice();
      },
    },
    'data.personalization.selectedCard': {
      handler() {
        this.updateCardPrice();
      },
      deep: true,
    },
    localQuantity: {
      handler() {
        this.updateCardPrice();
      },
    },
    cardPrice: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.clearTotalPriceCache();
        }
      },
    },
  },
  methods: {
    _getTotalPriceCacheKey() {
      return JSON.stringify({
        quantity: this.localQuantity,
        basePackage: this.data.basePackage?.id,
        products: this.localProducts.map((p) => ({ id: p.id, count: p.count })),
        foodDrinks: this.localFoodDrinks.map((f) => ({
          id: f.id,
          count: f.count,
        })),
        packaging: this.data.packaging?.id,
        packagingProduct: this.data.packagingProduct?.id,
        personalization: this.data.personalization?.selectedCard?.id,
        cardPrice: this.cardPrice, // Include cardPrice in cache key
        printing: this.data.personalization?.printing?.total_ex_vat,
        printingPerPackage:
          this.data.personalization?.printing?.per_package?.total_ex_vat,
      });
    },

    clearTotalPriceCache() {
      this._totalPriceCache = null;
    },

    setupEventListeners() {
      document.addEventListener('s7_config_updated', (e) => {
        if (e.detail?.quantity) {
          this.localQuantity = e.detail.quantity;
        }
      });
    },

    getBasePackagePriceExVat() {
      return 0;
    },

    getProductsPriceExVat() {
      if (!this.localProducts || this.localProducts.length === 0) return 0;

      return this.localProducts.reduce((sum, product) => {
        const count = Number(product.count) || 0;
        const price =
          product.price_excl_tax || product.price || product.regular_price || 0;

        if (
          this.data.packagingMode === 'product' &&
          this.data.packagingProduct &&
          product.id === this.data.packagingProduct.id
        ) {
          return sum;
        }

        return sum + price * count;
      }, 0);
    },

    getFoodDrinksPriceExVat() {
      if (!this.localFoodDrinks || this.localFoodDrinks.length === 0) return 0;

      return this.localFoodDrinks.reduce((sum, food) => {
        const count = Number(food.count) || 0;
        const price =
          food.price_excl_tax || food.price || food.regular_price || 0;

        if (
          this.data.packagingMode === 'product' &&
          this.data.packagingProduct &&
          food.id === this.data.packagingProduct.id
        ) {
          return sum;
        }

        return sum + price * count;
      }, 0);
    },

    getSelectedPackagingPriceExVat() {
      if (this.data.packagingMode === 'product' && this.data.packagingProduct) {
        return (
          this.data.packagingProduct.price_excl_tax ||
          this.data.packagingProduct.price ||
          this.data.packagingProduct.regular_price ||
          0
        );
      } else if (this.data.packagingMode === 'box' && this.data.packaging) {
        return (
          this.data.packaging.price_excl_tax ||
          this.data.packaging.price ||
          this.data.packaging.regular_price ||
          0
        );
      }
      return 0;
    },

    _isPackagingProductAlreadyInPackage(packagingProduct) {
      if (!packagingProduct || !this.data.basePackage) return false;

      const basePackageProducts = this.data.basePackage.products || [];
      const basePackageFoodDrinks = this.data.basePackage.foodDrinks || [];

      const isInBaseProducts = basePackageProducts.some(
        (p) => p.id === packagingProduct.id,
      );
      const isInBaseFoodDrinks = basePackageFoodDrinks.some(
        (f) => f.id === packagingProduct.id,
      );
      const isInLocalProducts = this.localProducts.some(
        (p) => p.id === packagingProduct.id,
      );
      const isInLocalFoodDrinks = this.localFoodDrinks.some(
        (f) => f.id === packagingProduct.id,
      );

      const result =
        isInBaseProducts ||
        isInBaseFoodDrinks ||
        isInLocalProducts ||
        isInLocalFoodDrinks;

      return result;
    },

    _isPackagingBoxAlreadyInPackage(packagingBox) {
      if (!packagingBox || !this.data.basePackage) return false;

      // Check if the packaging box is already in the base package
      const basePackagePackaging = this.data.basePackage.packaging;
      if (basePackagePackaging && basePackagePackaging.id === packagingBox.id) {
        return true;
      }

      // Check if the packaging box is in the base package products or food/drinks
      const basePackageProducts = this.data.basePackage.products || [];
      const basePackageFoodDrinks = this.data.basePackage.foodDrinks || [];
      const localProducts = this.localProducts || [];
      const localFoodDrinks = this.localFoodDrinks || [];

      const isInBaseProducts = basePackageProducts.some(
        (p) => p.id === packagingBox.id,
      );
      const isInBaseFoodDrinks = basePackageFoodDrinks.some(
        (f) => f.id === packagingBox.id,
      );
      const isInLocalProducts = localProducts.some(
        (p) => p.id === packagingBox.id,
      );
      const isInLocalFoodDrinks = localFoodDrinks.some(
        (f) => f.id === packagingBox.id,
      );

      const result =
        isInBaseProducts ||
        isInBaseFoodDrinks ||
        isInLocalProducts ||
        isInLocalFoodDrinks;

      return result;
    },

    getPersonalizationPriceExVat() {
      let price = 0;

      if (this.data.personalization?.printing?.per_package?.total_ex_vat) {
        price += this.data.personalization.printing.per_package.total_ex_vat;
      } else if (this.data.personalization?.printing?.total_ex_vat) {
        price +=
          this.data.personalization.printing.total_ex_vat /
          (this.localQuantity || 1);
      }

      if (this.data.personalization?.selectedCard) {
        const cardPriceToUse =
          this.cardPrice !== null
            ? this.cardPrice
            : this.data.personalization.selectedCard.price;
        price += cardPriceToUse;
      }

      if (this.data.personalization?.customDesignPrice) {
        price += this.data.personalization.customDesignPrice;
      }
      return price;
    },

    formatPrice,
    decodeHtml,
    getPersonalizedProductsItem() {
      if (
        !this.data.personalization?.productPersonalization ||
        !Object.values(this.data.personalization.productPersonalization).some(
          (item) => item && (item.sticker || item.zeefdruk || item.graveren),
        )
      ) {
        return {
          id: 'personalization',
          name: 'Geen producten geselecteerd voor bedrukking',
          price: 0,
          imageUrl: '',
          image: '',
          description: '',
          count: 0,
        };
      }

      // Use the same logic as personalizedProductsCount to count unique products
      const personalizedCount = this.personalizedProductsCount;

      // Haal drukwerk kosten op (per package) - use per_package data if available
      let printingCostsPerPackage = 0;

      if (this.data.personalization?.printing?.per_package?.total_ex_vat) {
        printingCostsPerPackage =
          this.data.personalization.printing.per_package.total_ex_vat;
      } else if (this.data.personalization?.printing?.total_ex_vat) {
        // Fallback to old calculation if per_package data is not available
        const totalPrintingCosts =
          this.data.personalization.printing.total_ex_vat;
        printingCostsPerPackage =
          totalPrintingCosts / (this.localQuantity || 1);
      }

      return {
        id: 'personalization',
        name: `Bedrukken van ${personalizedCount} product${personalizedCount !== 1 ? 'en' : ''}`,
        price: printingCostsPerPackage,
        imageUrl: '',
        image: '',
        description:
          printingCostsPerPackage > 0
            ? `€${this.formatPrice(printingCostsPerPackage)} (ex. BTW) per pakket`
            : 'Gratis bedrukking',
        count: 1,
      };
    },

    getSelectedCardItem() {
      if (!this.data.personalization?.selectedCard) {
        return {
          id: 'card',
          name: 'Geen kaart gekozen',
          price: 0,
          imageUrl: '',
          image: '',
          description: '',
          count: 0,
        };
      }

      const card = this.data.personalization.selectedCard;
      const cardPrice = this.cardPrice !== null ? this.cardPrice : card.price;

      return {
        ...card,
        name: card.name,
        price: cardPrice,
        imageUrl: card.image,
        image: card.image,
        description: `€${cardPrice.toFixed(2).replace('.', ',')} × ${this.localQuantity || 1}`,
        count: 1,
      };
    },

    async updateCardPrice() {
      if (!this.data.personalization?.selectedCard) {
        const oldPrice = this.cardPrice;
        this.cardPrice = null;
        if (oldPrice !== null) {
          this.clearTotalPriceCache();
        }
        return;
      }

      const cardQuantity = this.localQuantity || 1;
      const card = this.data.personalization.selectedCard;
      const originalPrice = card.originalPrice || card.price;

      try {
        const tierPrice = await getCardUnitPrice(cardQuantity);
        const newPrice = tierPrice > 0 ? tierPrice : originalPrice;

        if (this.cardPrice !== newPrice) {
          this.cardPrice = newPrice;
        }
      } catch {
        if (this.cardPrice !== originalPrice) {
          this.cardPrice = originalPrice;
        }
      }
    },
    hasPersonalizedProducts() {
      return (
        this.data.personalization?.selectedCard ||
        (this.data.personalization?.productPersonalization &&
          Object.values(this.data.personalization.productPersonalization).some(
            (item) => item && (item.sticker || item.zeefdruk || item.graveren),
          ))
      );
    },
    updateLocalProducts() {
      if (!Array.isArray(this.data.products)) {
        this.localProducts = [];
        return;
      }

      const filteredProducts = this.data.products
        .filter((p) => typeof p.count === 'number' && p.count > 0)
        .map((p) => ({
          ...p,
          imageUrl: p.imageUrl || p.image || '',
        }));

      this.localProducts = filteredProducts.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    },

    updateLocalFoodDrinks() {
      if (!Array.isArray(this.data.foodDrinks)) {
        this.localFoodDrinks = [];
        return;
      }

      const filteredFoodDrinks = this.data.foodDrinks
        .filter((f) => typeof f.count === 'number' && f.count > 0)
        .map((f) => ({
          ...f,
          imageUrl: f.imageUrl || f.image || '',
        }));

      this.localFoodDrinks = filteredFoodDrinks.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    },

    isStepCompleted(stepIndex) {
      const step = this.currentFlowSteps[stepIndex];
      if (!step) return false;
      switch (step.sidebarType) {
        case 'base':
          return !!this.data.basePackage;
        case 'products':
          return this.localProducts.length > 0;
        case 'foodDrinks':
          return this.localFoodDrinks.length > 0;
        case 'packaging':
          return (
            (this.data.packagingMode === 'product' &&
              !!this.data.packagingProduct) ||
            (this.data.packagingMode === 'box' && !!this.data.packaging)
          );
        case 'personalization':
          return this.hasPersonalizedProducts();
        default:
          return false;
      }
    },
    isActiveStep(stepIndex) {
      const sidebarStepIndex = this.getSidebarStepIndex(this.currentStep);
      return stepIndex === sidebarStepIndex;
    },
    isStepOpen(stepIndex) {
      return this.openSteps.has(stepIndex);
    },
    toggleStepCollapse(stepIndex) {
      if (this.openSteps.has(stepIndex)) {
        this.openSteps.delete(stepIndex);
      } else {
        this.openSteps.clear();
        this.openSteps.add(stepIndex);
      }
    },
    goToStep(stepIndex) {
      const componentStepIndex = this.getComponentStepIndex(stepIndex);

      if (componentStepIndex !== -1) {
        this.openSteps.clear();
        this.openSteps.add(stepIndex);
        this.$emit('go-to-step', componentStepIndex);
      }
    },
    hasNextStep(currentStepIndex) {
      return currentStepIndex < this.currentFlowSteps.length - 1;
    },
    getNextStepTitle(currentStepIndex) {
      const nextStep = this.currentFlowSteps[currentStepIndex + 1];
      return nextStep ? `${currentStepIndex + 2}. ${nextStep.title}` : '';
    },
    goToNextStep(currentStepIndex) {
      if (this.hasNextStep(currentStepIndex)) {
        const nextSidebarIndex = currentStepIndex + 1;
        const nextComponentIndex = this.getComponentStepIndex(nextSidebarIndex);

        if (nextComponentIndex !== -1) {
          this.openSteps.clear();
          this.openSteps.add(nextSidebarIndex);
          this.$emit('go-to-step', nextComponentIndex);
        }
      }
    },
    getComponentStepIndex(sidebarStepIndex) {
      const sidebarStep = this.currentFlowSteps[sidebarStepIndex];
      if (!sidebarStep) return -1;

      const allSteps = flowConfig[this.flowType]?.steps || [];
      return allSteps.findIndex((step) => step.id === sidebarStep.id);
    },
    getSidebarStepIndex(componentStepIndex) {
      const allSteps = flowConfig[this.flowType]?.steps || [];
      const componentStep = allSteps[componentStepIndex];
      if (!componentStep) return -1;

      return this.currentFlowSteps.findIndex(
        (step) => step.id === componentStep.id,
      );
    },
    updateItemCount(item, type, action) {
      const items =
        type === 'products' ? this.localProducts : this.localFoodDrinks;
      const foundItem = items.find((i) => i.id === item.id);

      if (!foundItem) return;

      if (action === 'increase') {
        foundItem.count = (foundItem.count || 0) + 1;
      } else if (action === 'decrease') {
        if (foundItem.count > 1) {
          foundItem.count--;
        } else {
          this.removeItem(item.id, type);
          return;
        }
      }

      this.emitUpdateItems(type);
    },
    getItemCount(itemId, type) {
      let items = [];
      if (type === 'products') items = this.localProducts;
      else if (type === 'foodDrinks') items = this.localFoodDrinks;
      const foundItem = items.find((i) => i.id === itemId);

      if (foundItem) return foundItem.count;
      else return null;
    },
    removeItem(itemId, type) {
      if (type === 'products') {
        this.localProducts = this.localProducts.filter((p) => p.id !== itemId);
        this.$emit('remove-product', itemId);
      } else if (type === 'foodDrinks') {
        this.localFoodDrinks = this.localFoodDrinks.filter(
          (f) => f.id !== itemId,
        );
        this.$emit('remove-food', itemId);
      } else if (type === 'packaging') {
        this.$emit('remove-packaging');
      } else if (type === 'card') {
        this.$emit('remove-card');
      }

      this.closeForegroundModal();
    },
    emitUpdateItems(type) {
      if (type === 'products') {
        this.emitUpdateProducts();
      } else if (type === 'foodDrinks') {
        this.emitUpdateFoodDrinks();
      }
    },
    increaseProductCount(product) {
      this.updateItemCount(product, 'products', 'increase');
    },
    decreaseProductCount(product) {
      this.updateItemCount(product, 'products', 'decrease');
    },
    removeProduct(productId) {
      this.removeItem(productId, 'products');
    },
    increaseFoodDrinkCount(item) {
      this.updateItemCount(item, 'foodDrinks', 'increase');
    },
    decreaseFoodDrinkCount(item) {
      this.updateItemCount(item, 'foodDrinks', 'decrease');
    },
    removeFoodDrink(id) {
      this.removeItem(id, 'foodDrinks');
    },
    emitUpdateProducts() {
      this.$emit('update-products', this.localProducts);
    },
    emitUpdateFoodDrinks() {
      this.$emit('update-fooddrinks', this.localFoodDrinks);
    },
    handleRemovePackage() {
      this.$emit('clear-draft');
      this.$emit('reset-package', false);
      this.$emit('show-package-type-modal');
    },
    handleRemoveBasePackage() {
      this.$emit('reset-package');
    },
    isLastStep(stepIndex) {
      return stepIndex === this.currentFlowSteps.length - 1;
    },
    handleAddToCart() {
      this.$emit('add-to-cart');
    },
    handleStepTitleClick(stepIndex) {
      this.goToStep(stepIndex);
    },
    addToCart() {
      try {
        if (
          this.localProducts.length === 0 &&
          this.localFoodDrinks.length === 0 &&
          !this.isBasePackage
        ) {
          alert(
            'Voeg minimaal één product toe voordat je het pakket kunt opslaan.',
          );
          return;
        }

        const hasProducts = this.localProducts.length > 0;
        const hasFoodDrinks = this.localFoodDrinks.length > 0;
        const hasBasePackage = this.isBasePackage;

        if (!hasProducts && !hasFoodDrinks && !hasBasePackage) {
          alert(
            'Voeg minimaal één product, eten/drinken of basispakket toe voordat je het pakket kunt opslaan.',
          );
          return;
        }

        if (!this.validateCartData()) {
          alert(
            'Er zijn problemen met je configuratie. Controleer alle velden.',
          );
          return;
        }

        const packageData = {
          packageName: this.basePackageTitle || 'Eigen samengesteld pakket',
          basePackage: this.data.basePackage || null,
          quantity: this.localQuantity,
          products: [...this.localProducts],
          foodDrinks: [...this.localFoodDrinks],
          packaging: (() => {
            if (this.data.packagingProduct && !this.data.packaging) {
              return this.data.packagingProduct;
            }

            return this.data.packaging || null;
          })(),
          packagingOptions: this.data.packagingOptions || [],
          packagingMode: this.data.packagingMode || 'box',
          packagingProduct: this.data.packagingProduct || null,
          personalization: {
            printworkCount: this.personalizedProductsCount,
            customDesign: this.data.personalization?.personalMessage
              ? true
              : false,
            customDesignPrice: this.data.personalization?.personalMessage
              ? 2.98
              : 0,
            productPersonalization:
              this.data.personalization?.productPersonalization || {},
            personalMessage: this.data.personalization?.personalMessage || '',
            selectedCard: this.data.personalization?.selectedCard || null,
            printNotes: this.data.personalization?.printNotes || '',
            logoAttachments: this.data.personalization?.logoAttachments || [],
            printing: this.data.personalization?.printing || null,
          },
          deliveryInfo: this.data.deliveryInfo || {},
          timestamp: Date.now(),
        };

        let existingPackages = [];
        const stored = localStorage.getItem('composedPackages');
        if (stored) {
          try {
            existingPackages = JSON.parse(stored);
            if (!Array.isArray(existingPackages)) {
              existingPackages = [];
            }
          } catch (error) {
            console.error('Error parsing existing packages:', error);
            existingPackages = [];
          }
        }

        existingPackages.push(packageData);
        localStorage.setItem(
          'composedPackages',
          JSON.stringify(existingPackages),
        );

        if (typeof window !== 'undefined') {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ ecommerce: null });

          const items = [];
          const basePackageId = packageData.basePackage?.id || 0;
          const basePackageName =
            packageData.basePackage?.name ||
            packageData.packageName ||
            'Onbekend pakket';

          const packageQuantity = packageData.quantity || 1;

          const filteredProducts = (packageData.products || []).filter(
            (product) => {
              if (
                (packageData.packaging &&
                  packageData.packaging.id === product.id) ||
                (packageData.packagingProduct &&
                  packageData.packagingProduct.id === product.id)
              ) {
                return false;
              }
              return true;
            },
          );

          const allProducts = [
            ...filteredProducts,
            ...(packageData.foodDrinks || []),
            ...(packageData.packaging ? [packageData.packaging] : []),
          ].filter(Boolean);

          allProducts.forEach((product) => {
            if (!product.id || !product.name) {
              return;
            }

            const productQuantityPer = product.quantity || product.count || 1;
            const productQuantity = productQuantityPer * packageQuantity;
            const productPrice = parseFloat(product.price || 0);

            items.push({
              item_id: product.id,
              item_name: product.name,
              quantity: productQuantity,
              price: productPrice,
              item_list_id: basePackageId,
              item_list_name: basePackageName,
              quantity_item_list: packageQuantity,
              quantity_per: productQuantityPer,
            });
          });

          let totalValue = items.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0,
          );

          let printingCostPerPackage = 0;
          if (
            packageData.personalization?.printing?.per_package?.total_ex_vat
          ) {
            printingCostPerPackage =
              packageData.personalization.printing.per_package.total_ex_vat;
          } else if (packageData.personalization?.printing?.total_ex_vat) {
            printingCostPerPackage =
              packageData.personalization.printing.total_ex_vat /
              packageQuantity;
          }

          const totalPrintingCost = printingCostPerPackage * packageQuantity;

          if (totalPrintingCost > 0) {
            items.push({
              item_id: 'printing',
              item_name: 'Bedrukking',
              quantity: packageQuantity,
              price: printingCostPerPackage,
              item_list_id: basePackageId,
              item_list_name: basePackageName,
              quantity_item_list: packageQuantity,
              quantity_per: 1,
            });
          }

          totalValue += totalPrintingCost;

          window.dataLayer.push({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'EUR',
              value: totalValue,
              items: items,
            },
          });
        }

        document.dispatchEvent(
          new CustomEvent('cart_item_added', {
            detail: {
              newItem: packageData,
              totalCount: existingPackages.length,
            },
          }),
        );

        alert('Pakket succesvol toegevoegd aan winkelmandje!');

        window.location.href = '/winkelmandje';
      } catch (error) {
        console.error('Error adding to cart:', error);
        alert(
          'Er is een probleem opgetreden bij het toevoegen aan winkelmandje. Probeer het opnieuw.',
        );
      }
    },

    updateQuantity(newQuantity) {
      this.localQuantity = newQuantity;
      this.$emit('update:packageQuantity', newQuantity);
    },

    handleQuantityChanged(event) {
      // Quantity changed event handled
    },

    handleQuantityInvalid(event) {
      // Quantity invalid event handled
    },

    handleQuantityCorrected(event) {
      // Quantity corrected event handled
    },

    handleDetailModalSelect(product, type) {
      if (!product?.id) {
        // @Todo Handle select, but should not happen since sidebar items are always selected
        return;
      }

      if (type === 'card') {
        this.removeItem(null, 'card');
      } else if (type === 'packaging') {
        this.removeItem(null, 'packaging');
      }
    },

    validateCartData() {
      if (
        !this.localQuantity ||
        this.localQuantity < 10 ||
        this.localQuantity > 99999
      ) {
        console.error('Invalid quantity:', this.localQuantity);
        return false;
      }

      for (const product of this.localProducts) {
        if (
          !product.id ||
          !product.name ||
          typeof product.price !== 'number' ||
          product.price < 0
        ) {
          console.error('Invalid product:', product);
          return false;
        }
        if (product.count && (product.count < 1 || product.count > 100)) {
          console.error('Invalid product count:', product);
          return false;
        }
      }

      for (const food of this.localFoodDrinks) {
        if (
          !food.id ||
          !food.name ||
          typeof food.price !== 'number' ||
          food.price < 0
        ) {
          console.error('Invalid food item:', food);
          return false;
        }
        if (food.count && (food.count < 1 || food.count > 100)) {
          console.error('Invalid food count:', food);
          return false;
        }
      }

      return true;
    },
    async handleLaunchConfetti() {
      await launchConfetti('#confetti-cta', [75, 125], [50, 70], { y: 0.5 });
    },
    closeForegroundModal() {
      if (this.detailModalDetails.length > 1) {
        this.detailModalDetails.pop();
      } else {
        this.detailModalDetails = [];
        this.showProductDetailModal = false;
      }
    },
    openNewModal(addedModalData) {
      this.detailModalDetails.push({
        ...addedModalData,
        count: this.getItemCount(
          addedModalData.product?.id,
          addedModalData.type,
        ),
      });

      // Show modal
      if (this.detailModalDetails.length > 0) {
        this.showProductDetailModal = true;
      }
    },
  },
};
</script>
